import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";

export interface Event {
  id: string;
  title: string;
  description: string;
  event_date: string;
  start_time?: string;
  end_time?: string;
  location?: string;
  max_attendees?: number;
  current_attendees?: number;
  status: string;
  is_public: boolean;
  image_url?: string;
  organizer_id?: number;
  category_id?: number;
  created_at: string;
  updated_at: string;
}

export interface EventsListParams {
  limit?: number;
  skip?: number;
  category_id?: number;
  status?: string;
  is_public?: boolean;
  event_date?: string;
  end_date?: string;
  search?: string;
}

export const eventsApi = createApi({
  reducerPath: "eventsApi",
  baseQuery: fetchBaseQuery({
    baseUrl: "http://localhost:8000/v1/events/",
    prepareHeaders: (headers) => {
      // Add auth headers here if needed
      headers.set("Content-Type", "application/json");
      return headers;
    },
  }),
  tagTypes: ["Event", "UpcomingEvents", "RegisteredEvents"],
  endpoints: (builder) => ({
    // Get all events with filtering
    getEvents: builder.query<Event[], EventsListParams>({
      query: (params = {}) => ({
        url: "",
        params,
      }),
      providesTags: ["Event"],
    }),

    // Get upcoming events
    getUpcomingEvents: builder.query<Event[], { limit?: number }>({
      query: ({ limit = 10 } = {}) => ({
        url: "upcoming",
        params: { limit },
      }),
      providesTags: ["UpcomingEvents"],
    }),

    // Get my registered events
    getMyRegisteredEvents: builder.query<Event[], { limit?: number }>({
      query: ({ limit = 10 } = {}) => ({
        url: "my-registered",
        params: { limit },
      }),
      providesTags: ["RegisteredEvents"],
    }),

    // Create new event
    createEvent: builder.mutation<Event, Partial<Event>>({
      query: (eventData) => ({
        url: "",
        method: "POST",
        body: eventData,
      }),
      invalidatesTags: ["Event", "UpcomingEvents"],
    }),

    // Update event
    updateEvent: builder.mutation<Event, { id: string; data: Partial<Event> }>({
      query: ({ id, data }) => ({
        url: `${id}`,
        method: "PUT",
        body: data,
      }),
      invalidatesTags: (_, __, { id }) => [
        { type: "Event", id },
        "Event",
        "UpcomingEvents",
      ],
    }),
  }),
});

export const {
  useGetEventsQuery,
  useGetUpcomingEventsQuery,
  useGetMyRegisteredEventsQuery,
  useCreateEventMutation,
  useUpdateEventMutation,
} = eventsApi;
